import { create } from 'zustand'
import { apiFetch } from '../api/client'
import { training as trainingApi } from '../api/training'

export const pathsApi = {
    list:   (params = {})  => {
        const qsParams = new URLSearchParams()
        for (const [key, value] of Object.entries(params)) {
            if (value == null || value === '') continue
            if (Array.isArray(value)) {
                value.filter(Boolean).forEach(item => qsParams.append(key, item))
            } else {
                qsParams.append(key, value)
            }
        }
        const qs = qsParams.toString()
        return apiFetch(`/training/paths/${qs ? `?${qs}` : ''}`)
    },
    get:    (id)           => apiFetch(`/training/paths/${id}`),
    my:     (empId)        => apiFetch(`/training/paths/my/${empId}`),
    create: (d)            => apiFetch('/training/paths/', { method: 'POST', body: JSON.stringify(d) }),
    step:   (pathId, d)    => apiFetch(`/training/paths/${pathId}/steps`, { method: 'POST', body: JSON.stringify(d) }),
    // Редактирование существующего шага: меняется только sequence / is_optional / notes
    // (смена самого элемента шага делается через delete+add — backend PATCH item не меняет).
    updateStep: (pathId, stepId, d) => apiFetch(`/training/paths/${pathId}/steps/${stepId}`, { method: 'PATCH', body: JSON.stringify(d) }),
    deleteStep: (pathId, stepId)    => apiFetch(`/training/paths/${pathId}/steps/${stepId}`, { method: 'DELETE' }),
    // Полиморфный каталог элементов трека: статьи (БЗ) + тесты + диалоги, с подписями.
    pathItems: ()          => apiFetch('/training/paths/path-items'),
    // Разделы-контейнеры трека (PR-2) + итоговый тест раздела (PR-2b).
    addSection:    (pathId, d)            => apiFetch(`/training/paths/${pathId}/sections`, { method: 'POST', body: JSON.stringify(d) }),
    updateSection: (pathId, sectionId, d) => apiFetch(`/training/paths/${pathId}/sections/${sectionId}`, { method: 'PATCH', body: JSON.stringify(d) }),
    deleteSection: (pathId, sectionId)    => apiFetch(`/training/paths/${pathId}/sections/${sectionId}`, { method: 'DELETE' }),
    sectionTestBuilder: (pathId, sectionId)    => apiFetch(`/training/paths/${pathId}/sections/${sectionId}/final-test/builder?_t=${Date.now()}`),
    saveSectionTest:    (pathId, sectionId, d) => apiFetch(`/training/paths/${pathId}/sections/${sectionId}/final-test`, { method: 'PUT', body: JSON.stringify(d) }),
    // Прохождение итогового теста раздела учеником (PR-4).
    sectionTestStart:  (pathId, sectionId)          => apiFetch(`/training/paths/${pathId}/sections/${sectionId}/final-test/start`, { method: 'POST' }),
    sectionTestSubmit: (pathId, sectionId, answers) => apiFetch(`/training/paths/${pathId}/sections/${sectionId}/final-test/submit`, { method: 'POST', body: JSON.stringify({ answers }) }),
    remove: (id)           => apiFetch(`/training/paths/${id}`, { method: 'DELETE' }),
    update: (id, d)        => apiFetch(`/training/paths/${id}`, { method: 'PATCH', body: JSON.stringify(d) }),
    deleteStep: (pathId, stepId) => apiFetch(`/training/paths/${pathId}/steps/${stepId}`, { method: 'DELETE' }),
    enroll: (pid, eid)     => apiFetch(`/training/paths/${pid}/enroll`, { method: 'POST', body: JSON.stringify({ employee_id: eid }) }),
    previewAudience: (filters) => apiFetch('/training/paths/preview-audience', { method: 'POST', body: JSON.stringify(filters) }),
    // Wave 2 PR 4 — versioning
    publish:     (id)            => apiFetch(`/training/paths/${id}/publish`, { method: 'POST' }),
    archive:     (id, reason)    => apiFetch(`/training/paths/${id}/archive`, { method: 'POST', body: JSON.stringify({ reason: reason || null }) }),
    createDraft: (id)            => apiFetch(`/training/paths/${id}/draft`, { method: 'POST' }),
    // Wave 2 PR 6 — cohort + retest (ICE #3, #4)
    cohort:      (pathId)        => apiFetch(`/training/paths/${pathId}/cohort`),
    retest:      (pathId, employeeId, moduleId) => apiFetch(`/training/paths/${pathId}/retest`, {
        method: 'POST',
        body: JSON.stringify({ employee_id: employeeId, module_id: moduleId || null }),
    }),
    // «Зачесть прочтение в трек»: статья прочитана в Базе знаний → зачесть как
    // пройденный шаг трека (явное действие сотрудника, только для статей без теста).
    creditReading: (pathId, articleId, employeeId) => apiFetch(`/training/paths/${pathId}/credit-reading`, {
        method: 'POST',
        body: JSON.stringify({ article_id: articleId, employee_id: employeeId }),
    }),
    notifyEmployee: (employeeId, message) => apiFetch('/reminders/send-now', {
        method: 'POST',
        body: JSON.stringify({ employee_id: employeeId, message_type: 'learning_reminder', custom_text: message }),
    }),
}

const useTrainingStore = create((set, get) => ({
    // ── Modules State
    modules: [],
    loadingModules: false,
    errorModules: null,
    // true — список взят из сохранённой копии, а не с сервера.
    modulesFromCache: false,

    fetchModules: async () => {
        set({ loadingModules: true, errorModules: null, modulesFromCache: false })
        try {
            const data = await trainingApi.modules()
            set({ modules: Array.isArray(data) ? data : [], loadingModules: false })
        } catch (e) {
            // Ошибка кладётся ЦЕЛИКОМ, а не `e.message`: код ответа нужен,
            // чтобы экран отличил отказ в доступе от поломки. Сведённая к
            // строке ошибка теряет `status`, и человеку без права страница
            // предлагала «Повторить» вместо похода к администратору.
            set({ errorModules: e, modules: [], modulesFromCache: false, loadingModules: false })
            // Сохранённая копия. Если она есть — показываем ЕЁ, а не экран
            // ошибки: данные на экране лучше пустого места. Но молчать про их
            // возраст нельзя, поэтому копия поднимает `modulesFromCache`, и
            // страница рисует плашку «показана сохранённая копия».
            try {
                const c = localStorage.getItem('onboardix_sync_training-modules')
                if (c) {
                    const d = JSON.parse(c).data
                    if (Array.isArray(d) && d.length > 0) {
                        set({ modules: d, modulesFromCache: true, errorModules: null })
                    }
                }
            } catch {}
        }
    },

    setActiveModule: (module) => set({ activeModule: module }),
    activeModule: null,

    // ── Paths State
    allPaths: [],
    myPaths: [],
    loadingPaths: false,
    errorPaths: null,

    fetchPaths: async (employeeId, catalogFilters = {}) => {
        set({ loadingPaths: true, errorPaths: null })
        try {
            const catalog = await pathsApi.list(catalogFilters)
            let mine = []
            if (employeeId) {
                mine = await pathsApi.my(employeeId)
            }
            set({
                allPaths: Array.isArray(catalog) ? catalog : [],
                myPaths: Array.isArray(mine) ? mine : [],
                loadingPaths: false,
            })
        } catch (e) {
            // Ошибка целиком — см. комментарий в `fetchModules`.
            set({ errorPaths: e, allPaths: [], myPaths: [], loadingPaths: false })
        }
    },

    enrollPath: async (pathId, employeeId) => {
        if (!employeeId) return;
        try {
            await pathsApi.enroll(pathId, employeeId)
            await get().fetchPaths(employeeId)
        } catch (e) {
            // Отказ ЗАПИСИ на трек — не отказ загрузки списка: он больше не
            // подменяет список экраном ошибки. Сообщение показывает сам
            // `PathsTab` строкой рядом с кнопкой (`enrollError`).
            throw e
        }
    },

    deletePath: async (pathId) => {
        try {
            await pathsApi.remove(pathId)
            const employeeId = get().myPaths[0]?.employee_id || null // Note: employeeId needed
            await get().fetchPaths(employeeId)
        } catch (e) {
            throw e
        }
    },

    updatePath: async (pathId, data, employeeId) => {
        try {
            await pathsApi.update(pathId, data)
            await get().fetchPaths(employeeId)
        } catch (e) {
            throw e
        }
    },

    // ── Progress State
    progress: {},
    loadingProgress: false,

    fetchProgress: async (employeeId) => {
        if (!employeeId) return;
        set({ loadingProgress: true })
        try {
            const data = await trainingApi.progress(employeeId)
            set({ progress: data, loadingProgress: false })
        } catch (e) {
            set({ loadingProgress: false })
            try {
                const c = localStorage.getItem('onboardix_sync_training-progress')
                if (c) {
                    const d = JSON.parse(c).data
                    if (d) set({ progress: d })
                }
            } catch {}
        }
    },

    updateProgressLocally: (moduleId, scenarioId, score, moduleRef) => {
        set((state) => {
            const prevProgress = state.progress;
            const p = { ...(prevProgress[moduleId] || { status: 'in_progress', completed: [], score: {} }) }
            
            const newCompleted = p.completed.includes(scenarioId) ? p.completed : [...p.completed, scenarioId]
            const newScore = { ...p.score, [scenarioId]: score }
            
            const allScenarios = moduleRef?.scenarios || [];
            const allDone = allScenarios.length > 0 && allScenarios.every(s => newCompleted.includes(s.id))
            
            return { 
                progress: {
                    ...prevProgress, 
                    [moduleId]: { 
                        ...p, 
                        completed: newCompleted, 
                        score: newScore, 
                        status: allDone ? 'completed' : 'in_progress', 
                        ...(allDone ? { completed_at: new Date().toISOString().slice(0, 10), coins_earned: (moduleRef?.is_critical || moduleRef?.difficulty === 'critical') ? 100 : 30 } : {}) 
                    }
                } 
            }
        });
    }
}))

export default useTrainingStore
